// convert minutes to hours and minutes format
export const formatRuntime = (minutes) => {
  if (!minutes) return 'N/A';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  
  return `${hours}h ${mins}m`;
};

// convert date to readable format with IST adjustment
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    // Parse the UTC date string
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    // Adjust for IST (UTC+5:30)
    const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
    const istDate = new Date(date.getTime() + istOffset);
    
    // Format using browser's locale settings with IST time
    return istDate.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }) + ' IST';
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

// get year from date string
export const extractYear = (dateString) => {
  if (!dateString) return '';
  
  // Parse the UTC date string and convert to local timezone
  const date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return '';
  }
  
  return date.getFullYear().toString();
};

// format rating with one decimal place
export const formatRating = (rating) => {
  if (!rating && rating !== 0) return 'N/A';
  
  return Number(rating).toFixed(1);
};

// convert progress to percentage
export const formatProgress = (progress) => {
  if (progress === undefined || progress === null) return '0%';
  return `${Math.round(progress)}%`;
};

// calculate relative time from date
export const timeAgo = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Parse the UTC date string
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }
    
    // Adjust for IST (UTC+5:30)
    const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
    const istDate = new Date(date.getTime() + istOffset);
    
    // Get current time
    const now = new Date();
    
    // Calculate the actual time difference in seconds
    const seconds = Math.floor((now.getTime() - istDate.getTime()) / 1000);
    
    let interval = Math.floor(seconds / 31536000);
    if (interval >= 1) {
      return interval === 1 ? '1 year ago' : `${interval} years ago`;
    }
    
    interval = Math.floor(seconds / 2592000);
    if (interval >= 1) {
      return interval === 1 ? '1 month ago' : `${interval} months ago`;
    }
    
    interval = Math.floor(seconds / 86400);
    if (interval >= 1) {
      return interval === 1 ? '1 day ago' : `${interval} days ago`;
    }
    
    interval = Math.floor(seconds / 3600);
    if (interval >= 1) {
      return interval === 1 ? '1 hour ago' : `${interval} hours ago`;
    }
    
    interval = Math.floor(seconds / 60);
    if (interval >= 1) {
      return interval === 1 ? '1 minute ago' : `${interval} minutes ago`;
    }
    
    return seconds < 10 ? 'just now' : `${Math.floor(seconds)} seconds ago`;
  } catch (error) {
    console.error('Error calculating time ago:', error);
    return '';
  }
};

// convert date to local date and time format
export const formatLocalDateTime = (dateString) => {
  if (!dateString) return 'just now';
  
  try {
    // Parse the UTC date string
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    // Adjust for IST (UTC+5:30)
    const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
    const istDate = new Date(date.getTime() + istOffset);
    
    // Get current time also adjusted for IST for consistent comparison
    const now = new Date();
    
    // Calculate the actual time difference in seconds
    const diffInSeconds = Math.floor((now.getTime() - istDate.getTime()) / 1000);
    
    // Less than a minute
    if (diffInSeconds < 60) {
      return 'just now';
    }
    
    // Less than an hour
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    
    // Less than a day
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }
    
    // Less than 2 days - show "Yesterday" with time
    if (diffInSeconds < 172800) {
      return `Yesterday at ${istDate.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })}`;
    }
    
    // Less than a week
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }
    
    // Use local date format with time for older dates
    return istDate.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Unknown date';
  }
};